import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import { setActiveLocale } from '@/lib/utils'

import cs from './locales/cs.json'
import de from './locales/de.json'
import en from './locales/en.json'
import fr from './locales/fr.json'
import it from './locales/it.json'
import pl from './locales/pl.json'
import sk from './locales/sk.json'

/** Everything the language switcher offers - keep in sync with `resources`. */
export const LANGUAGES = ['sk', 'en', 'de', 'fr', 'it', 'pl', 'cs'] as const
export type Language = (typeof LANGUAGES)[number]

/**
 * Keys are kept flat and mirror the legacy `messages.*` namespace from
 * app/lang/messages.sk_SK.neon, so translations can be ported over directly.
 */
void i18n.use(initReactI18next).init({
  resources: {
    sk: { translation: sk },
    en: { translation: en },
    de: { translation: de },
    fr: { translation: fr },
    it: { translation: it },
    pl: { translation: pl },
    cs: { translation: cs },
  },
  lng: storedLanguage() ?? 'sk',
  fallbackLng: 'sk',
  interpolation: { escapeValue: false },
})

/** A stale value from before a language was removed must not brick the app. */
function storedLanguage(): Language | null {
  const stored = localStorage.getItem('l2s:lang')
  return LANGUAGES.includes(stored as Language) ? (stored as Language) : null
}

// Dates and money follow the interface language, not just the strings.
setActiveLocale(i18n.language)

/** Jazyk zvolený na tomto zariadení, inak prvý jazyk prehliadača, ktorý
 * aplikácia pozná - `null`, keď nič z toho. Pre stránky bez prihlásenia
 * (mobilný pohľad inštruktora), kde jazyk školy príde až s dátami. */
export function preferredLanguage(): Language | null {
  const stored = storedLanguage()
  if (stored) return stored
  for (const tag of navigator.languages ?? [navigator.language]) {
    const code = tag.slice(0, 2).toLowerCase() as Language
    if (LANGUAGES.includes(code)) return code
  }
  return null
}

/** Prepnúť jazyk bez zapamätania (na rozdiel od `setLanguage`). */
export function applyLanguage(lang: string) {
  if (!LANGUAGES.includes(lang as Language) || i18n.language === lang) return
  setActiveLocale(lang)
  void i18n.changeLanguage(lang)
}

export function setLanguage(lang: string) {
  localStorage.setItem('l2s:lang', lang)
  setActiveLocale(lang)
  void i18n.changeLanguage(lang)
}

export default i18n
